import { useMemo } from 'react';

import { Link } from '@mui/material';
import pluralize from 'pluralize';

import {
  Box,
  Column,
  EllipsisWithTooltip,
  LayoutCard,
  NoData,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  NoResults,
  Search,
  SearchableTable,
  SearchProvider,
  useSearchContext,
} from '@cast/shared-search-ui';
import { K8sProvider } from '@cast/shared-types';
import type { BillingV1FeatureEntityUsage } from '@cast/types';

import { Link as RouterLink } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';

import { getEntityKey } from './UsageOverTimeChart';

type EntitiesTableProps = {
  entities: BillingV1FeatureEntityUsage[];
  entityColorMap?: Map<string, string>;
  clusterProviderMap?: Map<string, K8sProvider>;
  unit: string;
  showShareOfAllUsage?: boolean;
  totalUsage?: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  selectedRows: string[];
  onRowsSelected: (ids: string[]) => void;
  maxSelection?: number;
};

const EntitiesTableTitle = () => {
  const searchCtx = useSearchContext();
  const count = searchCtx?.filteredData?.length ?? 0;

  if (searchCtx?.isLoading) {
    return (
      <Typography variant="A1" color={tokens.theme.fg.structural.primary}>
        Clusters
      </Typography>
    );
  }

  return (
    <Typography
      variant="A1"
      color={tokens.theme.fg.structural.primary}
      data-testid="records-count"
    >
      {count} {pluralize('cluster', count)}
    </Typography>
  );
};

export const EntitiesTable = ({
  entities,
  entityColorMap,
  clusterProviderMap,
  unit,
  showShareOfAllUsage = false,
  totalUsage,
  isLoading,
  isError,
  refetch,
  selectedRows,
  onRowsSelected,
  maxSelection,
}: EntitiesTableProps) => {
  const sorted = useMemo(
    () => [...entities].sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0)),
    [entities]
  );

  const usageTotal = useMemo(() => {
    if (!showShareOfAllUsage) {
      return 0;
    }
    if (totalUsage != null && totalUsage > 0) {
      return totalUsage;
    }
    return sorted.reduce((sum, entity) => sum + (entity.usage ?? 0), 0);
  }, [showShareOfAllUsage, sorted, totalUsage]);

  const handleRowsSelected = (ids: string[]) => {
    const limit = maxSelection ?? Infinity;
    const retained = selectedRows.filter((id) => ids.includes(id));
    const added = ids.filter((id) => !selectedRows.includes(id));
    const merged = [...retained, ...added].slice(0, limit);
    onRowsSelected(merged);
  };

  return (
    <SearchProvider<BillingV1FeatureEntityUsage>
      urlKey="platform-usage-entities"
      data={sorted}
      isLoading={isLoading}
      freeTextPredicate={(item, text) =>
        !!(
          item.entityName?.toLowerCase().includes(text.toLowerCase()) ||
          item.entityId?.toLowerCase().includes(text.toLowerCase())
        )
      }
    >
      <LayoutCard
        title={<EntitiesTableTitle />}
        testId="platform-usage-entities-card"
        padding={false}
        slotProps={{
          header: { py: 12 },
          content: {
            gap: 12,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box px={16}>
          <Search placeholder="Enter search keywords" />
        </Box>

        <SearchableTable<BillingV1FeatureEntityUsage>
          cacheKey={tableCacheKeys.PLATFORM_USAGE.ENTITIES}
          testId="platform-usage-entities-table"
          rowKey={(item) => getEntityKey(item)}
          fillParentHeight
          hasFailed={isError}
          selectedRows={selectedRows}
          onRowsSelected={(ids) => handleRowsSelected(ids as string[])}
          components={{
            noData: <NoData entity="cluster" />,
            noResults: <NoResults entity="cluster" />,
            failed: <FailedToLoad refresh={refetch} />,
          }}
        >
          <Column<BillingV1FeatureEntityUsage>
            id="name"
            header="Name"
            minWidth={320}
            sortable
            accessor="entityName"
            renderCell={(item) => {
              const key = getEntityKey(item);
              const color = entityColorMap?.get(key);
              const providerType = item.entityId
                ? clusterProviderMap?.get(item.entityId)
                : undefined;
              return (
                <Stack direction="row" alignItems="center" gap={8}>
                  {color ? (
                    <Box
                      sx={{
                        width: 2,
                        height: 32,
                        borderRadius: '2px',
                        bgcolor: color,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box sx={{ width: 2, flexShrink: 0 }} />
                  )}
                  <K8sProviderIcon provider={providerType} size={16} />
                  <Stack gap={2} sx={{ minWidth: 0 }}>
                    {item.entityId ? (
                      <EllipsisWithTooltip
                        TypographyProps={{
                          variant: 'P12M',
                          color: tokens.theme.fg.selection.primary,
                        }}
                      >
                        <Link
                          component={RouterLink}
                          to={`/external-clusters/${item.entityId}`}
                          underline="none"
                          sx={{
                            typography: 'P12M',
                            color: tokens.theme.fg.selection.primary,
                          }}
                        >
                          {item.entityName ?? '–'}
                        </Link>
                      </EllipsisWithTooltip>
                    ) : (
                      <EllipsisWithTooltip
                        TypographyProps={{ variant: 'P12R' }}
                      >
                        {item.entityName ?? '–'}
                      </EllipsisWithTooltip>
                    )}
                    {item.entityId && (
                      <Typography
                        variant="P12R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        {item.entityId}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              );
            }}
          />
          <Column<BillingV1FeatureEntityUsage>
            id="usage"
            header="Usage"
            infoTooltip={`Average monthly billable ${unit} for this cluster. Provisioned usage is normalized, meaning a ${unit} running 15 days in a 30-day month counts as 0.5 billable ${unit}.`}
            width={220}
            sortable
            accessor="usage"
            headerCellWrapperSx={{ justifyContent: 'flex-end' }}
            cellWrapperSx={{ textAlign: 'right' }}
            renderCell={(item) => (
              <Stack
                direction="row"
                alignItems="baseline"
                gap={4}
                justifyContent="flex-end"
              >
                <Typography
                  color={tokens.theme.fg.structural.primary}
                  variant="P12M"
                >
                  {Math.round(item.usage ?? 0).toLocaleString('en-US')}
                </Typography>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {unit}
                </Typography>
              </Stack>
            )}
          />
          {showShareOfAllUsage && (
            <Column<BillingV1FeatureEntityUsage>
              id="share"
              header="Share of all usage"
              width={180}
              sortable
              sortingType="number"
              accessor={(item) =>
                usageTotal > 0 ? ((item.usage ?? 0) / usageTotal) * 100 : 0
              }
              headerCellWrapperSx={{ justifyContent: 'flex-end' }}
              cellWrapperSx={{ textAlign: 'right' }}
              renderCell={(item) => {
                const share =
                  usageTotal > 0 ? ((item.usage ?? 0) / usageTotal) * 100 : 0;
                return (
                  <Typography
                    variant="P12M"
                    color={tokens.theme.fg.structural.primary}
                  >
                    {share.toLocaleString('en-US', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                    %
                  </Typography>
                );
              }}
            />
          )}
        </SearchableTable>
      </LayoutCard>
    </SearchProvider>
  );
};
